import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { CloudService, ServiceId, MetricHistorySlot } from '../../core/services/cloud.service';

export interface MetricColumn {
  key:   string;
  label: string;
}

/**
 * Shared drill-down panel: the last 24h of CloudWatch metric history (hourly)
 * for a single resource of a metric-driven service (API Gateway, EC2, Cognito,
 * AppSync, DynamoDB). Self-loads via CloudService whenever the bound resourceId
 * changes, so each detail component only needs to pass serviceId/resourceId and
 * the metric columns to render.
 */
@Component({
  selector: 'app-cloud-metrics-panel',
  template: `
    <div class="flex h-full flex-col">
      <!-- Header -->
      <div class="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <lucide-icon name="clock" [size]="15" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-semibold text-slate-700">Metric history</p>
          <p class="truncate font-mono text-[10px] text-slate-400">{{ resourceId }}</p>
        </div>
        <button
          type="button"
          (click)="closed.emit()"
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close metric history"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <!-- Loading -->
      <div *ngIf="loading" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading metrics…
      </div>

      <!-- Error -->
      <p *ngIf="!loading && error" class="px-4 py-4 text-xs text-amber-700">
        Couldn't load metric history. The cross-account role may be missing
        <span class="font-mono">cloudwatch:GetMetricData</span>, or the metrics aren't published yet.
      </p>

      <!-- List -->
      <div *ngIf="!loading && !error" class="flex-1 overflow-y-auto">
        <div *ngIf="slots.length === 0" class="px-4 py-6 text-xs text-slate-400">
          No metric datapoints in the last 24 hours.
        </div>

        <table *ngIf="slots.length > 0" class="w-full text-left text-xs">
          <thead class="sticky top-0 bg-white">
            <tr class="border-b border-slate-200">
              <th class="px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Time</th>
              <th *ngFor="let m of metrics" class="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-400">{{ m.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let slot of slots" class="border-b border-slate-100 last:border-0">
              <td class="whitespace-nowrap px-4 py-1.5 text-[11px] text-slate-500">{{ slot.timestamp | cloudDate }}</td>
              <td *ngFor="let m of metrics" class="px-2 py-1.5 text-right font-mono text-[11px] text-slate-700">{{ fmt(slot.values[m.key]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `,
})
export class CloudMetricsPanelComponent implements OnChanges, OnDestroy {
  @Input() serviceId!: ServiceId;
  @Input() resourceId: string | null = null;
  @Input() metrics: MetricColumn[] = [];

  @Output() closed = new EventEmitter<void>();

  slots: MetricHistorySlot[] = [];
  loading = false;
  error   = false;

  private sub?: Subscription;

  constructor(private readonly cloud: CloudService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resourceId'] || changes['serviceId']) {
      if (this.resourceId) this.load();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private load(): void {
    if (!this.resourceId) return;
    this.loading = true;
    this.error   = false;
    this.slots   = [];
    this.sub?.unsubscribe();
    this.sub = this.cloud.getResourceMetricHistory(this.serviceId, this.resourceId).subscribe(history => {
      this.loading = false;
      if (history === null) { this.error = true; return; }
      this.slots = history.slots;
    });
  }

  fmt(v: number | null): string {
    return v === null || v === undefined ? '—' : String(Math.round(v));
  }
}
